import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SendIcon from '@mui/icons-material/Send';

import { toast } from '@src/components/core/toaster';
import {
  discardAdAccountCard,
  fetchAdAccountCard,
  markAdAccountCardApplied,
  requestAdAccountCard,
  revealAdAccountCard,
} from '@src/api/cardVault';

const fmtDateTime = (iso) => (iso
  ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
  : '');
const fmtDate = (iso) => (iso
  ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  : '');
const formatCardNumber = (digits) => (digits || '').replace(/(\d{4})(?=\d)/g, '$1 ');
const brandLabel = (brand) => {
  const map = { visa: 'Visa', mastercard: 'Mastercard', amex: 'Amex', discover: 'Discover', diners: 'Diners', jcb: 'JCB' };
  return map[brand] || 'Card';
};

const STATUS_CHIP = {
  pending: { label: 'Waiting for you', color: 'warning' },
  revealed: { label: 'Reveal window open', color: 'info' },
  applied: { label: 'Added to ad account', color: 'success' },
  expired: { label: 'Expired unused', color: 'default' },
  withdrawn: { label: 'Withdrawn by client', color: 'default' },
  superseded: { label: 'Replaced', color: 'default' },
  discarded: { label: 'Discarded', color: 'default' },
};

function copy(text, label) {
  if (!navigator?.clipboard) return;
  navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`)).catch(() => {});
}

function SecretRow({ label, value, mono = true, copyable = true }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.75 }}>
      <Typography variant="caption" color="text.secondary" sx={{ width: 110, flexShrink: 0, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{ fontFamily: mono ? 'ui-monospace, Menlo, monospace' : 'inherit', fontWeight: 600, letterSpacing: mono ? 1 : 0, flex: 1, userSelect: 'all' }}
      >
        {value || '—'}
      </Typography>
      {copyable && value && (
        <Tooltip title={`Copy ${label.toLowerCase()}`}>
          <IconButton size="small" onClick={() => copy(value, label)}><ContentCopyIcon fontSize="inherit" /></IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}

/**
 * Staff-facing card on the onboarding workflow page.
 *
 * Reveal is a deliberate, confirmed, logged action. The decrypted card
 * lives only in this component's state while the dialog is open and is
 * dropped the moment it closes; marking it "added" wipes it server-side.
 */
export default function AdAccountCardStaffCard({ clientSlug, clientName }) {
  const [status, setStatus] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [secret, setSecret] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    if (!clientSlug) return;
    try {
      const res = await fetchAdAccountCard(clientSlug);
      setStatus(res?.data || {});
    } catch {
      setStatus({ loadFailed: true });
    }
  }, [clientSlug]);

  useEffect(() => { load(); }, [load]);

  const submission = status?.submission || null;
  const lastClosed = status?.history?.[0] || null;
  const events = status?.events || [];
  const adAccounts = status?.ad_accounts || [];
  const windowMinutes = status?.reveal_window_minutes || 15;

  const handleReveal = async () => {
    if (!submission) return;
    setBusy('reveal');
    try {
      const res = await revealAdAccountCard(submission.id);
      setSecret(res?.data || null);
      setConfirmOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not reveal the card.');
      setConfirmOpen(false);
      load();
    } finally {
      setBusy('');
    }
  };

  const closeSecret = () => setSecret(null);

  const handleApplied = async () => {
    if (!submission) return;
    setBusy('apply');
    try {
      await markAdAccountCardApplied(submission.id);
      setSecret(null);
      toast.success('Marked as added — the stored card details are wiped.');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not mark the card as added.');
    } finally {
      setBusy('');
    }
  };

  const handleDiscard = async () => {
    if (!submission) return;
    const reason = window.prompt('Discard this card without using it? Add a short reason for the audit trail:', '');
    if (reason === null) return;
    setBusy('discard');
    try {
      await discardAdAccountCard(submission.id, reason);
      setSecret(null);
      toast.success('Card discarded and wiped.');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not discard the card.');
    } finally {
      setBusy('');
    }
  };

  const handleRequest = async () => {
    setBusy('request');
    try {
      const res = await requestAdAccountCard(clientSlug);
      const to = res?.data?.sent_to || [];
      toast.success(to.length ? `Request emailed to ${to.join(', ')}` : 'Request emailed');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send the request email.');
    } finally {
      setBusy('');
    }
  };

  const chip = submission
    ? STATUS_CHIP[submission.status]
    : lastClosed
      ? STATUS_CHIP[lastClosed.status]
      : { label: 'Not submitted', color: 'default' };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <LockIcon fontSize="small" color="action" />
            <Typography variant="subtitle1" fontWeight={700}>Ad Account Card</Typography>
          </Stack>
          {chip && <Chip size="small" label={chip.label} color={chip.color} sx={{ fontSize: 11 }} />}
        </Stack>

        {status === null ? (
          <CircularProgress size={18} />
        ) : status.loadFailed ? (
          <Alert severity="warning" sx={{ py: 0.5 }}>Could not load the card status.</Alert>
        ) : (
          <Stack spacing={1.5}>
            {status.configured === false && (
              <Alert severity="error" sx={{ py: 0.5 }}>
                CARD_VAULT_KEYS is not set on this server — clients cannot submit and nothing can be revealed.
              </Alert>
            )}

            {submission ? (
              <Box>
                <Typography variant="body2" sx={{ fontSize: 13 }}>
                  <strong>{brandLabel(submission.brand)} •••• {submission.last4}</strong> · exp {String(submission.exp_month).padStart(2, '0')}/{submission.exp_year}
                  {' · '}submitted {fmtDateTime(submission.submitted_at)}{submission.submitted_by ? ` by ${submission.submitted_by}` : ''}
                </Typography>
                {submission.status === 'revealed' ? (
                  <Typography variant="caption" color="info.main" sx={{ display: 'block' }}>
                    Revealed {submission.reveal_count}× — last by {submission.revealed_by || 'staff'} at {fmtDateTime(submission.last_revealed_at)}.
                    Wiped automatically at {fmtDateTime(submission.reveal_window_ends_at)} unless marked added first.
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Wiped automatically on {fmtDate(submission.expires_at)} if not used. Reveal it, add it to the ad account, then mark it added.
                  </Typography>
                )}
              </Box>
            ) : lastClosed?.status === 'applied' ? (
              <Typography variant="body2" sx={{ fontSize: 13 }}>
                <strong>{brandLabel(lastClosed.brand)} •••• {lastClosed.last4}</strong> added to the ad account {fmtDateTime(lastClosed.applied_at)}
                {lastClosed.applied_by ? ` by ${lastClosed.applied_by}` : ''}. Card details are no longer stored.
              </Typography>
            ) : lastClosed ? (
              <Typography variant="body2" color="warning.main" sx={{ fontSize: 13 }}>
                Last card ({brandLabel(lastClosed.brand)} •••• {lastClosed.last4}) ended as “{STATUS_CHIP[lastClosed.status]?.label || lastClosed.status}” on {fmtDate(lastClosed.purged_at || lastClosed.submitted_at)}. Ask the client to submit again.
              </Typography>
            ) : (
              <Typography variant="body2" color="warning.main" sx={{ fontSize: 13 }}>
                Not submitted yet. The client enters it in the onboarding wizard (Billing step) or via the link below.
              </Typography>
            )}

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {submission && (
                <Button
                  size="small" variant="contained" startIcon={<LockOpenIcon />}
                  onClick={() => setConfirmOpen(true)} disabled={!!busy}
                >
                  {submission.status === 'revealed' ? 'Reveal again' : 'Reveal card'}
                </Button>
              )}
              {submission && submission.status === 'revealed' && (
                <Button size="small" variant="outlined" color="success" onClick={handleApplied} disabled={!!busy}>
                  Mark added to Facebook
                </Button>
              )}
              {submission && (
                <Button size="small" color="error" onClick={handleDiscard} disabled={!!busy}>
                  Discard
                </Button>
              )}
              {!submission && (
                <Button size="small" variant="outlined" startIcon={<SendIcon />} onClick={handleRequest} disabled={!!busy}>
                  {busy === 'request' ? 'Sending…' : 'Email client a request'}
                </Button>
              )}
              {status.card_url && (
                <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => copy(status.card_url, 'Client link')}>
                  Copy client link
                </Button>
              )}
            </Stack>

            {adAccounts.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {adAccounts.map((a) => (
                  <Button
                    key={a.account_id} size="small" component="a" href={a.billing_url} target="_blank" rel="noopener noreferrer"
                    endIcon={<OpenInNewIcon fontSize="inherit" />} sx={{ textTransform: 'none', fontSize: 12 }}
                  >
                    Billing settings · act_{a.account_id}{a.name ? ` (${a.name})` : ''}
                  </Button>
                ))}
              </Stack>
            )}

            {events.length > 0 && (
              <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 32, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                  <Typography variant="caption" color="text.secondary">Audit trail ({events.length})</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, pt: 0 }}>
                  <Stack spacing={0.25}>
                    {events.map((e) => (
                      <Typography key={e.id} variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                        {fmtDateTime(e.created_at)} · {e.action_label}{e.actor ? ` · ${e.actor}` : ''}
                        {e.detail?.reason ? ` · ${e.detail.reason}` : ''}
                      </Typography>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}
          </Stack>
        )}
      </CardContent>

      {/* Confirm reveal */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reveal {clientName ? `${clientName}'s` : 'the'} card?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This is logged under your name. Have the Facebook ad account billing page open — you get {windowMinutes} minutes
            from the first reveal before the details are wiped, or sooner when you mark it added.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleReveal} disabled={busy === 'reveal'} startIcon={<LockOpenIcon />}>
            {busy === 'reveal' ? 'Revealing…' : 'Reveal'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revealed card */}
      <Dialog open={!!secret} onClose={closeSecret} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <LockOpenIcon color="warning" />
            <Typography variant="h6">{brandLabel(secret?.brand)} •••• {secret?.last4}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1.5, py: 0.5 }}>
            Add this to the ad account now. Wiped at {fmtDateTime(secret?.reveal_window_ends_at)} or when you click “Added to Facebook”.
          </Alert>
          <SecretRow label="Name on card" value={secret?.cardholder_name} mono={false} />
          <SecretRow label="Card number" value={formatCardNumber(secret?.card_number)} />
          <SecretRow
            label="Expires"
            value={secret ? `${String(secret.exp_month).padStart(2, '0')}/${secret.exp_year}` : ''}
          />
          <SecretRow label="CVC" value={secret?.cvc} />
          <SecretRow label="Billing ZIP" value={secret?.billing_zip} />
          {adAccounts.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
              {adAccounts.map((a) => (
                <Button
                  key={a.account_id} size="small" variant="outlined" component="a" href={a.billing_url} target="_blank" rel="noopener noreferrer"
                  endIcon={<OpenInNewIcon fontSize="inherit" />} sx={{ textTransform: 'none' }}
                >
                  Open billing · act_{a.account_id}
                </Button>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSecret}>Close (keep stored for now)</Button>
          <Button variant="contained" color="success" onClick={handleApplied} disabled={busy === 'apply'}>
            {busy === 'apply' ? 'Wiping…' : 'Added to Facebook — wipe it'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
